// ============================================================
// ITLU MEE JAFFAR — SINGLE EDITABLE CONTENT SOURCE
// ------------------------------------------------------------
// Edit ALL site content here: text (English + Telugu), photos,
// curated YouTube videos, brand logos and social URLs.
// The Interviews grid and Stats also pull LIVE data from the
// YouTube channel via the backend (/api/youtube/*).
// ============================================================

export const SITE = {
  name: "ITLU MEE JAFFAR",
  youtube: "https://www.youtube.com/@itlumeejaffar",
  // Leave empty ("") to hide a social link everywhere on the site.
  instagram: "https://www.instagram.com/itlu_mee_jaffar/",
  facebook: "https://www.facebook.com/Jaffar.Reporter/",
  twitter: "",
  // Official contact details.
  email: "contact@itlumeejaffar.in",
  phone: "+91 6302-575056",
  phoneHref: "tel:+916302575056",
  whatsapp: "https://wa.me/916302575056",
};

// Photos supplied by Jaffar (stored in /frontend/public/photos/).
export const IMAGES = {
  hero: "/photos/studio.jpg",
  about: "/photos/jaffar-portrait.jpg",
  aboutWithCbn: "/photos/cbn.jpg",
  social: "/photos/untold.jpg",
  groundDump: "/photos/ground-dump.jpg",
  groundHouse: "/photos/ground-house.jpg",
};

// Hero intro film (supplied by Jaffar), encoded for web in /frontend/public/video/.
export const VIDEO = {
  intro: "/video/intro.mp4",
  introMobile: "/video/intro-mobile.mp4",
  poster: "/video/intro-poster.jpg",
};

// Real videos from the Itlu Mee Jaffar channel. Thumbnails come straight from YouTube.
const yt = (id, title) => ({
  id,
  title,
  url: `https://www.youtube.com/watch?v=${id}`,
  image: `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`,
  imageFallback: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
});

export const VIDEOS = {
  ratCurry: yt("rmM_krB5dCQ", "MLA KU CHALLENGE: ఎలుకల కర్రీ తింటారా?"),
  biggBoss: yt("C-DLXwtUgU0", "Bigg Boss Script Leaked?! Divvela Madhuri Exposes The Truth & Challenges Pawan Kalyan"),
  rowdies: yt("mb3z0goN4e0", "The Dark Side Of Hyderabad Rowdies | Amberpet Shankar | Jaffar Files"),
  mohanBabu: yt("k3L-3tuBTGk", "Mohan Babu Reacts To The Manchu Brothers Issue"),
  vizag: yt("rgxtOAX_ot8", "Google's Secret Vizag Deal — Jackpot Or Trap For Andhra?"),
  wormPuri: yt("_bS4t_gnOsg", "హెల్త్ మంత్రికి పురుగుల పూరి.. తింటారా?"),
  jagapathi: yt("oPn7YLUtJSk", "కష్టం మాది… పేరు మాత్రం హీరోలదేనా? | Jagapathi Babu Unfiltered"),
  naraLokesh: yt("RI2Y2A7JiTY", "Exclusive Interview With Nara Lokesh | AP Election | Who Is Next CM?"),
  rgv: yt("Yu-2Ohy9iZI", "Ram Gopal Varma's Exclusive Interview"),
  // "Stories Worth Watching" card (Social section). Video supplied by Jaffar; title to be updated when it goes public.
  storiesWorthWatching: yt("yFOaZYNYVJY", "Itlu Mee Jaffar — Latest Story"),
};

// Last known public channel figures (used only if the live YouTube feed is unreachable).
export const CHANNEL_SNAPSHOT = { subscribers: 919000, videos: 890, topRecentViews: 1097945, totalViews: 278570723 };

// [EDITABLE] Brand collaborations shown below the stats (shared by both languages).
// Drop official logo files into /frontend/public/brands/ and point `logo` to them.
// Logos are rendered monochrome automatically. Set `logo: ""` to show the name as text.
export const BRANDS = [
  { name: "Lalitha Jewellery", logo: "/brands/lalitha.svg" },
  { name: "KLM", logo: "/brands/klm.png" },
  { name: "CMR", logo: "" },
  { name: "Policybazaar", logo: "/brands/policybazaar.png" },
  { name: "Ditto Insurance", logo: "/brands/ditto.png" },
  { name: "My Dr Pain Releaf Oil", logo: "" },
  { name: "Marwadi University", logo: "/brands/marwadi.svg" },
  { name: "GD Goenka University", logo: "/brands/goenka.svg" },
];

export const CONTENT = {
  en: {
    nav: {
      links: [
        { id: "home", label: "Home" },
        { id: "people", label: "People" },
        { id: "interviews", label: "Interviews" },
        { id: "ground-reports", label: "Ground Reports" },
        { id: "politics", label: "Politics" },
        { id: "stories", label: "Stories" },
        { id: "about", label: "About Jaffar" },
        { id: "blog", label: "Blog", to: "/blog" },
        { id: "contact", label: "Contact", action: "contact" },
      ],
      watchLatest: "Watch Latest",
      searchPlaceholder: "Search interviews, stories, topics…",
      searchEmpty: "No matches yet — try another word.",
    },
    hero: {
      eyebrow: "TELUGU JOURNALISM • INTERVIEWS • REAL STORIES",
      line1: "Every Voice Has",
      line2: "A Story To Tell.",
      description:
        "Powerful conversations, untold stories, and ground realities that deserve to be heard.",
      primaryCta: "Watch Latest Interview",
      secondaryCta: "Explore Stories",
      scrollHint: "Scroll",
      unmute: "Unmute video",
      mute: "Mute video",
    },
    marquee: [
      "INTERVIEWS",
      "GROUND REPORTS",
      "DOCUMENTARIES",
      "PUBLIC-INTEREST JOURNALISM",
      "REAL STORIES",
      "ఇట్లు మీ జాఫర్",
    ],
    interviews: {
      eyebrow: "LATEST CONVERSATIONS",
      title: "Conversations",
      titleItalic: "That Matter.",
      description:
        "In-depth interviews with the people shaping politics, society, and public opinion.",
      viewAll: "View All On YouTube",
      liveTag: "Latest On YouTube",
      views: "views",
      items: [
        { ...VIDEOS.jagapathi, guest: "Jagapathi Babu", category: "Unfiltered" },
        { ...VIDEOS.biggBoss, guest: "Divvela Madhuri", category: "Exclusive" },
        { ...VIDEOS.naraLokesh, guest: "Nara Lokesh", category: "Politics" },
        { ...VIDEOS.rgv, guest: "Ram Gopal Varma", category: "Exclusive" },
        { ...VIDEOS.mohanBabu, guest: "Mohan Babu", category: "Cinema" },
        { ...VIDEOS.rowdies, guest: "Amberpet Shankar", category: "Jaffar Files" },
        { ...VIDEOS.vizag, guest: "Explainer", category: "Public Affairs" },
      ],
    },
    people: {
      eyebrow: "THE PEOPLE BEHIND THE STORIES",
      title: "The People Behind",
      titleItalic: "The Stories.",
      description:
        "Every conversation brings a new perspective. Explore the people, creators and public figures whose stories connect with Jaffar's work.",
      hint: "Hover to explore · Click a portrait for the full profile",
      hintTouch: "Swipe to explore · Tap a portrait for the full profile",
      openProfile: "Open profile",
      watchStory: "Watch the conversation",
      visitChannel: "Visit the channel",
      connectionLabel: "Connection to Jaffar",
      storyLabel: "Related story",
      socialLabel: "Follow",
      prev: "Previous person",
      next: "Next person",
      close: "Close profile",
      relationship: {
        host: "The Host",
        interview: "Interview Guest",
        featured: "Featured On Itlu Mee Jaffar",
        collaborator: "Collaborator",
      },
    },
    ground: {
      eyebrow: "STORIES FROM THE GROUND",
      title: "Beyond The",
      titleItalic: "Headlines.",
      description: "Real people. Real problems. Stories that need to be heard.",
      readStory: "Watch More On YouTube",
      items: [
        {
          image: IMAGES.groundDump,
          url: SITE.youtube,
          title: "Reporting From The Dump Yard",
          category: "Ground Report",
          description:
            "Jaffar on location at a waste dump site — camera in hand, reporting the realities communities live beside.",
        },
        {
          image: IMAGES.groundHouse,
          url: SITE.youtube,
          title: "Inside A Crumbling Home",
          category: "Human Stories",
          description: "A field visit to a worn-down tiled house — the kind of story that rarely makes the headlines.",
        },
        {
          ...VIDEOS.ratCurry,
          category: "Public Issues",
          description: "A challenge to an MLA that drew over a million views on YouTube.",
        },
      ],
    },
    politics: {
      eyebrow: "POLITICS & PUBLIC AFFAIRS",
      title: "The Questions",
      titleItalic: "That Matter.",
      description:
        "Political interviews, public policy and governance — the conversations behind the headlines.",
      filters: [
        { id: "all", label: "All" },
        { id: "governance", label: "Governance" },
        { id: "public-issues", label: "Public Issues" },
        { id: "exclusive", label: "Exclusive Interviews" },
      ],
      items: [
        { ...VIDEOS.ratCurry, category: "public-issues" },
        { ...VIDEOS.vizag, category: "governance" },
        { ...VIDEOS.biggBoss, category: "exclusive" },
        { ...VIDEOS.wormPuri, category: "public-issues" },
        { ...VIDEOS.naraLokesh, category: "exclusive" },
        { ...VIDEOS.rowdies, category: "exclusive" },
      ],
      filterLabels: {
        governance: "Governance",
        "public-issues": "Public Issues",
        exclusive: "Exclusive",
      },
    },
    featured: {
      eyebrow: "JAFFAR FILES",
      ...VIDEOS.rowdies,
      description:
        "A long-form look at the dark side of Hyderabad's rowdy culture, with Amberpet Shankar. Watch the full story on YouTube.",
      cta: "Watch on YouTube",
    },
    about: {
      eyebrow: "THE JOURNALIST BEHIND THE STORIES",
      title: "Asking Questions.",
      titleItalic: "Bringing Stories Forward.",
      description:
        "ITLU MEE JAFFAR brings bold investigative stories, social experiments and real-life narratives to millions of viewers through independent journalistic storytelling.",
      bio: "Jaffar is a seasoned journalist from India known for fearless storytelling and authentic content. With a strong background in media, he has worked across television, digital platforms and field reporting, and gained national recognition as a contestant on Bigg Boss Telugu Season 3. His journey extends to the Telugu film industry as an actor. Through 'Itlu Mee Jaffar', he delivers investigative stories, social experiments and real-life narratives that matter.",
      cta: "Watch on YouTube",
      collab: "For collaborations & stories",
    },
    contact: {
      label: "Contact",
      eyebrow: "GET IN TOUCH",
      title: "Let's Talk.",
      description: "For interviews, collaborations, story tips and press — reach Jaffar directly.",
      collab: "For collaborations, stories & press",
      call: "Call",
      whatsapp: "WhatsApp",
      email: "Email",
      follow: "Follow",
      whatsappText: "Hi Jaffar, I'm reaching out from itlumeejaffar.in —",
    },
    blog: {
      eyebrow: "DAILY JOURNAL",
      title: "Latest",
      titleItalic: "Writing.",
      description: "Daily notes, field observations and news updates — written by Jaffar.",
      viewAll: "Read all posts",
      pageEyebrow: "BLOG & NEWS",
      pageTitle: "Notes From",
      pageTitleItalic: "The Field.",
      pageDescription: "Daily blog posts and news updates from Itlu Mee Jaffar.",
      filters: { all: "All", blog: "Blog", news: "News" },
      categories: { blog: "Blog", news: "News" },
      empty: "No posts yet. Check back soon.",
      readMore: "Read post",
      back: "Back to all posts",
      backHome: "Back to home",
      notFound: "This post could not be found.",
      watchVideo: "Watch the related video",
      loading: "Loading…",
    },
    stats: {
      eyebrow: "THE JOURNEY SO FAR",
      labels: {
        totalViews: "Total Channel Views",
        subscribers: "YouTube Subscribers",
        videos: "Videos Published",
        topViews: "Views On Top Recent Story",
      },
      brandsLabel: "Brands We've Worked With",
      brands: BRANDS,
    },
    social: {
      eyebrow: "WATCH & FOLLOW",
      title: "Stories Worth",
      titleItalic: "Watching.",
      description:
        "New interviews, documentaries and ground reports — published first on YouTube.",
      cta: "Subscribe on YouTube",
    },
    footer: {
      description:
        "Independent Telugu journalism — interviews, ground reports and real stories that deserve to be heard.",
      explore: "Explore",
      watch: "Watch",
      connect: "Connect",
      rights: "All rights reserved.",
    },
  },

  te: {
    nav: {
      links: [
        { id: "home", label: "హోమ్" },
        { id: "people", label: "వ్యక్తులు" },
        { id: "interviews", label: "ఇంటర్వ్యూలు" },
        { id: "ground-reports", label: "గ్రౌండ్ రిపోర్ట్స్" },
        { id: "politics", label: "రాజకీయాలు" },
        { id: "stories", label: "కథలు" },
        { id: "about", label: "జాఫర్ గురించి" },
        { id: "blog", label: "బ్లాగ్", to: "/blog" },
        { id: "contact", label: "సంప్రదించండి", action: "contact" },
      ],
      watchLatest: "తాజావి చూడండి",
      searchPlaceholder: "ఇంటర్వ్యూలు, కథలు వెతకండి…",
      searchEmpty: "ఫలితాలు లేవు — మరో పదం ప్రయత్నించండి.",
    },
    hero: {
      eyebrow: "తెలుగు జర్నలిజం • ఇంటర్వ్యూలు • నిజమైన కథలు",
      line1: "ప్రతి గొంతుకకూ",
      line2: "ఒక కథ ఉంది.",
      description:
        "శక్తివంతమైన సంభాషణలు, చెప్పబడని కథలు మరియు వినిపించాల్సిన నేల నిజాలు.",
      primaryCta: "తాజా ఇంటర్వ్యూ చూడండి",
      secondaryCta: "కథలను అన్వేషించండి",
      scrollHint: "కిందికి",
      unmute: "వీడియో శబ్దం ఆన్",
      mute: "వీడియో శబ్దం ఆఫ్",
    },
    marquee: [
      "ఇంటర్వ్యూలు",
      "గ్రౌండ్ రిపోర్ట్స్",
      "డాక్యుమెంటరీలు",
      "ప్రజా హిత జర్నలిజం",
      "నిజమైన కథలు",
      "ITLU MEE JAFFAR",
    ],
    interviews: {
      eyebrow: "తాజా సంభాషణలు",
      title: "ముఖ్యమైన",
      titleItalic: "సంభాషణలు.",
      description:
        "రాజకీయాలు, సమాజం మరియు ప్రజా అభిప్రాయాన్ని ఏర్పరిచే వ్యక్తులతో లోతైన ఇంటర్వ్యూలు.",
      viewAll: "YouTubeలో అన్నీ చూడండి",
      liveTag: "YouTubeలో తాజావి",
      views: "వీక్షణలు",
      items: [
        { ...VIDEOS.jagapathi, guest: "జగపతి బాబు", category: "అన్‌ఫిల్టర్డ్" },
        { ...VIDEOS.biggBoss, guest: "దివ్వెల మాధురి", category: "ప్రత్యేకం" },
        { ...VIDEOS.naraLokesh, guest: "నారా లోకేశ్", category: "రాజకీయాలు" },
        { ...VIDEOS.rgv, guest: "రామ్ గోపాల్ వర్మ", category: "ప్రత్యేకం" },
        { ...VIDEOS.mohanBabu, guest: "మోహన్ బాబు", category: "సినిమా" },
        { ...VIDEOS.rowdies, guest: "అంబర్‌పేట శంకర్", category: "జాఫర్ ఫైల్స్" },
        { ...VIDEOS.vizag, guest: "వివరణ", category: "ప్రజా వ్యవహారాలు" },
      ],
    },
    people: {
      eyebrow: "కథల వెనుక వ్యక్తులు",
      title: "కథల వెనుక",
      titleItalic: "వ్యక్తులు.",
      description:
        "ప్రతి సంభాషణ కొత్త దృక్కోణాన్ని తెస్తుంది. జాఫర్ కృషితో ముడిపడిన వ్యక్తులు, క్రియేటర్లు, ప్రముఖులను తెలుసుకోండి.",
      hint: "హోవర్ చేసి చూడండి · పూర్తి ప్రొఫైల్ కోసం ఫోటోపై క్లిక్ చేయండి",
      hintTouch: "స్వైప్ చేసి చూడండి · పూర్తి ప్రొఫైల్ కోసం ఫోటోపై ట్యాప్ చేయండి",
      openProfile: "ప్రొఫైల్ చూడండి",
      watchStory: "సంభాషణ చూడండి",
      visitChannel: "ఛానెల్ చూడండి",
      connectionLabel: "జాఫర్‌తో అనుబంధం",
      storyLabel: "సంబంధిత కథనం",
      socialLabel: "అనుసరించండి",
      prev: "మునుపటి వ్యక్తి",
      next: "తదుపరి వ్యక్తి",
      close: "ప్రొఫైల్ మూసివేయండి",
      relationship: {
        host: "హోస్ట్",
        interview: "ఇంటర్వ్యూ అతిథి",
        featured: "ఇట్లు మీ జాఫర్‌లో ప్రత్యేకం",
        collaborator: "సహకారి",
      },
    },
    ground: {
      eyebrow: "నేల మీద నుంచి కథలు",
      title: "హెడ్‌లైన్స్ కంటే",
      titleItalic: "అవతల.",
      description: "నిజమైన ప్రజలు. నిజమైన సమస్యలు. వినిపించాల్సిన కథలు.",
      readStory: "YouTubeలో మరిన్ని చూడండి",
      items: [
        {
          image: IMAGES.groundDump,
          url: SITE.youtube,
          title: "చెత్త డంపింగ్ యార్డ్ నుంచి రిపోర్ట్",
          category: "గ్రౌండ్ రిపోర్ట్",
          description: "చెత్త డంపింగ్ ప్రదేశంలో జాఫర్ — కెమెరా చేతబట్టి, ప్రజలు పక్కనే జీవిస్తున్న వాస్తవాలను చూపిస్తూ.",
        },
        {
          image: IMAGES.groundHouse,
          url: SITE.youtube,
          title: "శిథిలమవుతున్న ఇంటి లోపల",
          category: "మానవ కథలు",
          description: "పాడుబడిన పెంకుటింటికి క్షేత్ర పర్యటన — హెడ్‌లైన్స్‌లోకి అరుదుగా వచ్చే కథ.",
        },
        {
          ...VIDEOS.ratCurry,
          category: "ప్రజా సమస్యలు",
          description: "ఒక MLAకు విసిరిన సవాలు — YouTubeలో పది లక్షలకు పైగా వీక్షణలు.",
        },
      ],
    },
    politics: {
      eyebrow: "రాజకీయాలు & ప్రజా వ్యవహారాలు",
      title: "ముఖ్యమైన",
      titleItalic: "ప్రశ్నలు.",
      description:
        "రాజకీయ ఇంటర్వ్యూలు, ప్రజా విధానాలు మరియు పాలన — హెడ్‌లైన్స్ వెనుక సంభాషణలు.",
      filters: [
        { id: "all", label: "అన్నీ" },
        { id: "governance", label: "పాలన" },
        { id: "public-issues", label: "ప్రజా సమస్యలు" },
        { id: "exclusive", label: "ప్రత్యేక ఇంటర్వ్యూలు" },
      ],
      items: [
        { ...VIDEOS.ratCurry, category: "public-issues" },
        { ...VIDEOS.vizag, category: "governance" },
        { ...VIDEOS.biggBoss, category: "exclusive" },
        { ...VIDEOS.wormPuri, category: "public-issues" },
        { ...VIDEOS.naraLokesh, category: "exclusive" },
        { ...VIDEOS.rowdies, category: "exclusive" },
      ],
      filterLabels: {
        governance: "పాలన",
        "public-issues": "ప్రజా సమస్యలు",
        exclusive: "ప్రత్యేకం",
      },
    },
    featured: {
      eyebrow: "జాఫర్ ఫైల్స్",
      ...VIDEOS.rowdies,
      description:
        "హైదరాబాద్ రౌడీ సంస్కృతి చీకటి కోణంపై అంబర్‌పేట శంకర్‌తో సుదీర్ఘ సంభాషణ. పూర్తి కథ YouTubeలో చూడండి.",
      cta: "YouTubeలో చూడండి",
    },
    about: {
      eyebrow: "కథల వెనుక జర్నలిస్ట్",
      title: "ప్రశ్నలు అడుగుతూ.",
      titleItalic: "కథలను ముందుకు తీసుకువస్తూ.",
      description:
        "ITLU MEE JAFFAR స్వతంత్ర జర్నలిస్టిక్ కథనం ద్వారా ధైర్యమైన పరిశోధనాత్మక కథలు, సామాజిక ప్రయోగాలు మరియు నిజ జీవిత కథనాలను లక్షలాది ప్రేక్షకులకు అందిస్తుంది.",
      bio: "జాఫర్ నిర్భయ కథనం, ప్రామాణిక కంటెంట్‌కు పేరుగాంచిన అనుభవజ్ఞుడైన జర్నలిస్ట్. టెలివిజన్, డిజిటల్ వేదికలు మరియు క్షేత్ర రిపోర్టింగ్‌లో బలమైన నేపథ్యం ఉన్న ఆయన బిగ్ బాస్ తెలుగు సీజన్ 3 కంటెస్టెంట్‌గా జాతీయ గుర్తింపు పొందారు. నటుడిగా తెలుగు సినీ పరిశ్రమలోనూ ప్రయాణం కొనసాగించారు. 'ఇట్లు మీ జాఫర్' ద్వారా పరిశోధనాత్మక కథలు, సామాజిక ప్రయోగాలు మరియు నిజ జీవిత కథనాలను అందిస్తున్నారు.",
      cta: "YouTubeలో చూడండి",
      collab: "సహకారాలు & కథల కోసం",
    },
    contact: {
      label: "సంప్రదించండి",
      eyebrow: "సంప్రదించండి",
      title: "మాట్లాడుకుందాం.",
      description: "ఇంటర్వ్యూలు, సహకారాలు, కథల సమాచారం, ప్రెస్ కోసం — జాఫర్‌ను నేరుగా సంప్రదించండి.",
      collab: "సహకారాలు, కథలు & ప్రెస్ కోసం",
      call: "కాల్",
      whatsapp: "వాట్సాప్",
      email: "ఈమెయిల్",
      follow: "అనుసరించండి",
      whatsappText: "హాయ్ జాఫర్, itlumeejaffar.in నుంచి సంప్రదిస్తున్నాను —",
    },
    blog: {
      eyebrow: "డైలీ జర్నల్",
      title: "తాజా",
      titleItalic: "రచనలు.",
      description: "జాఫర్ రాసిన రోజువారీ గమనికలు, క్షేత్ర పరిశీలనలు, వార్తా అప్‌డేట్‌లు.",
      viewAll: "అన్ని పోస్టులు చదవండి",
      pageEyebrow: "బ్లాగ్ & వార్తలు",
      pageTitle: "క్షేత్రం నుంచి",
      pageTitleItalic: "గమనికలు.",
      pageDescription: "ఇట్లు మీ జాఫర్ నుంచి రోజువారీ బ్లాగ్ పోస్టులు, వార్తా అప్‌డేట్‌లు.",
      filters: { all: "అన్నీ", blog: "బ్లాగ్", news: "వార్తలు" },
      categories: { blog: "బ్లాగ్", news: "వార్తలు" },
      empty: "ఇంకా పోస్టులు లేవు. త్వరలో చూడండి.",
      readMore: "పోస్ట్ చదవండి",
      back: "అన్ని పోస్టులకు",
      backHome: "హోమ్‌కి",
      notFound: "ఈ పోస్ట్ కనబడలేదు.",
      watchVideo: "సంబంధిత వీడియో చూడండి",
      loading: "లోడ్ అవుతోంది…",
    },
    stats: {
      eyebrow: "ఇప్పటివరకు ప్రయాణం",
      labels: {
        totalViews: "మొత్తం ఛానెల్ వీక్షణలు",
        subscribers: "YouTube సబ్‌స్క్రైబర్లు",
        videos: "ప్రచురించిన వీడియోలు",
        topViews: "టాప్ తాజా కథనం వీక్షణలు",
      },
      brandsLabel: "మేము పనిచేసిన బ్రాండ్‌లు",
      brands: BRANDS,
    },
    social: {
      eyebrow: "చూడండి & అనుసరించండి",
      title: "చూడదగిన",
      titleItalic: "కథలు.",
      description:
        "కొత్త ఇంటర్వ్యూలు, డాక్యుమెంటరీలు మరియు గ్రౌండ్ రిపోర్ట్స్ — ముందుగా YouTubeలో.",
      cta: "YouTubeలో సబ్‌స్క్రయిబ్",
    },
    footer: {
      description:
        "స్వతంత్ర తెలుగు జర్నలిజం — వినిపించాల్సిన ఇంటర్వ్యూలు, గ్రౌండ్ రిపోర్ట్స్ మరియు నిజమైన కథలు.",
      explore: "అన్వేషణ",
      watch: "చూడండి",
      connect: "అనుసంధానం",
      rights: "అన్ని హక్కులు ప్రత్యేకించబడినవి.",
    },
  },
};
